import { ControlsContainer, GraphType, SigmaContainer, ZoomControl } from '@react-sigma/core';
import { Attributes, FC, useContext, useEffect, useState } from 'react';
import { GraphViewContext } from './Index';
import {
  Graph as GraphAPIType,
  SpecificNodeProperties,
  Relationship,
  Node,
} from '@/lib/api/APITypes';
import { createEdgeArrowProgram } from 'sigma/rendering';
import Graph from 'graphology';
import GraphEvents from './Events';
import { incrementRefreshCounter, removeFloatingDoc } from '@/lib/state/slices/ProjectStateSlice';
import { copyToMutableGraph } from '@/lib/GraphFactory';
import { useDispatch } from 'react-redux';
import { NodeBorderProgram } from '@sigma/node-border';
import FloatingDocument from './FloatingDocument';
import NodeContextMenu from './controls/NodeContextMenu';
import forceAtlas2 from 'graphology-layout-forceatlas2';
import FA2Layout from 'graphology-layout-forceatlas2/worker';
import { Button, IconButton, Stack } from '@chakra-ui/react';
import {
  LayoutForceAtlas2Control,
  useWorkerLayoutForceAtlas2,
} from '@react-sigma/layout-forceatlas2';
import { Box, Pause, Play, PlayCircle, RefreshCw, StopCircle } from 'react-feather';
import { useSystemTheme } from '@/lib/ThemeSwitcher';

function FA2Control(props: { reset: Function }) {
  const { start, kill, stop } = useWorkerLayoutForceAtlas2({ settings: { slowDown: 10 } });
  const [running, setRunning] = useState(false);
  const { system } = useSystemTheme();

  
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 5,
        left: 5,
        padding: 5,
        backgroundColor: system.tokens.getVar('colors.cardDefault'),
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      {running ? (
        <IconButton
          onClick={() => {
            stop();
            setRunning(false);
          }}
        >
          <Pause />
        </IconButton>
      ) : (
        <IconButton
          onClick={() => {
            start();
            setRunning(true);
          }}
        >
          <Play />
        </IconButton>
      )}
      <IconButton ms={2} onClick={() => props.reset()}>
        <RefreshCw />
      </IconButton>
    </div>
  );
}

export default function ForceDirectedLayout(props: {
  loadNeighbours: Function;
  loadRecommendedNeighbours: Function;
}) {
  const dispatch = useDispatch();
  const graphView = useContext(GraphViewContext);
  const [graph, setGraph] = useState<Graph<Node, Attributes, Attributes>>(
    new Graph({ type: 'mixed' }),
  );

  function loadGraphFromSearializedGraph() {
    if (!graphView.serializedGraph) {
      console.error('Serialzied Graph is null. Not my job to load it.');
    } else {
      let newGraph = copyToMutableGraph(
        Graph.from(graphView.serializedGraph) as Graph<Node, Attributes, Attributes>,
      );

      setGraph(newGraph);
      dispatch(incrementRefreshCounter());
    }
  }

  useEffect(() => {
    loadGraphFromSearializedGraph();
  }, [graphView.serializedGraph]);

  const [nodeContextMenu, setNodeContextMenu] = useState<{
    nodeId: string;
    id: string;
  }>();

  return (
    <SigmaContainer
      style={{ height: '100%', width: '100%', display: 'flex', position: 'relative' }}
      settings={{
        hoverRenderer: () => {},
        defaultEdgeType: 'arrow',
        edgeProgramClasses: {
          arrow: createEdgeArrowProgram({
            lengthToThicknessRatio: 6,
            widenessToThicknessRatio: 6,
          }),
        },
        nodeProgramClasses: {
          border: NodeBorderProgram,
        },
        zoomingRatio: 1.4,
        enableCameraRotation: false,
        allowInvalidContainer: true,
        renderEdgeLabels: false,
      }}
      key={`sigmaContainer${graphView.id}`}
      graph={graph}
    >
      <GraphEvents
        onHoverNode={() => {}}
        isActive={true}
        setNodeContextMenu={setNodeContextMenu}
        dragAndDrop={true}
      />
      {graphView.floatingDocuments.map((doc) => (
        <FloatingDocument
          elementId={doc.nodeId}
          key={doc.id}
          id={doc.id}
          onClose={() => {
            dispatch(
              removeFloatingDoc({
                graphViewId: graphView.id,
                floatingDocId: doc.id,
              }),
            );
          }}
        />
      ))}
      <FA2Control reset={loadGraphFromSearializedGraph} />
      {/* <TimeFilter style={{ position: 'absolute', bottom: '20px', left: '2%' }} /> */}
      {/* <QuickFilter style={{ position: 'absolute', top: '20px', left: '20px' }} /> */}
      {nodeContextMenu ? (
        <NodeContextMenu
          id={nodeContextMenu.id}
          nodeId={nodeContextMenu.nodeId}
          key={nodeContextMenu.nodeId}
          loadNeighbours={(id: string) => {
            props.loadNeighbours({ id, level: 1 });
          }}
          recommendNeighbours={(id: string, nodeId: string) => {
            props.loadRecommendedNeighbours(id);
          }}
        />
      ) : null}
    </SigmaContainer>
  );
}
